// Dependencies
import React from "react";

import {
	IconDefinition,
	faCheckCircle,
	faInfoCircle,
	faTimesCircle,
	faWarning,
} from "@fortawesome/pro-regular-svg-icons";

// components
import Icon from "../Icon";
// Styles
import css from "./Toast.module.scss";

export interface IToast {
	/**
	 * An id for the toast. This is generated automatically using nanoid.
	 */
	id: string;
	/**
	 * The message to display in the toast.
	 * @example "Hello World"
	 */
	message: string;
	/**
	 * The type of toast to display.
	 */
	type: "success" | "error" | "warning" | "info";
	/**
	 * The duration to display the toast for.
	 * @default 5000
	 */
	duration?: number;
	/**
	 * The size of the toast.
	 * @default "small"
	 */
	size?: "small" | "medium" | "large";
	/**
	 * icon to display in the toast.
	 */
	icon?: IconDefinition;
}

interface BaseProps extends IToast {
	/**
	 * Callback function when the toast is closed.
	 */
	onClose: () => void;
	/**
	 * Callback function when the toast is finished animating out.
	 */
	onFinish: () => void;
	/**
	 * The index of the toast in the toast container. Used to determine the
	 * position of the toast.
	 */
	positionIndex: number;
	/**
	 * Whether or not the toast is being hovered over.
	 * Used to determine whether or not to pause the timer.
	 */
	hasHover: boolean;
	/**
	 * Icon to display in the toast.
	 */
	icon?: IconDefinition;
	/**
	 *  Size of the toast
	 */
	size?: "small" | "medium" | "large";
}

interface StaticToastProps extends Pick<BaseProps, "message" | "type" | "size" | "icon"> {
	contentStyle?: React.CSSProperties;
	handleClick?: () => void;
	handleHoverState?: (nextState: boolean) => void;
}

const TypeIcon = (props: { type: string }) => {
	switch (props.type) {
		case "error":
			return faTimesCircle;
		case "success":
			return faCheckCircle;
		case "warning":
			return faWarning;
		case "info":
			return faInfoCircle;
		default:
			return null;
	}
};

/**
 * The toast message component without it being bound to the snackbar. This
 * allows us to render it statically on the page. Look at storybook for an
 * example of this.
 * @deprecated Use the Toast component from the py-components package instead
 */
export const StaticToast = (props: StaticToastProps) => {
	const {
		icon,
		message,
		type,
		contentStyle,
		handleClick = () => {},
		handleHoverState = () => {},
		size = "medium",
	} = props;

	const toastIcon = icon || (TypeIcon({ type }) as IconDefinition);

	return (
		<div
			className={`${css.staticToast} ${css[`type-${type}`]} ${css[size]}`}
			onClick={handleClick}
			onMouseEnter={() => handleHoverState(true)}
			onMouseLeave={() => handleHoverState(false)}
			style={contentStyle}
		>
			<Icon className={css.icon} size="regular" icon={toastIcon} />

			<div className={` ${css.toastMessage} `}>{message}</div>
		</div>
	);
};
